import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';

import { errorToast, successToast } from '../../common/utils/alert';
import { errorMessages } from '../../common/utils/constants';
import CreateProjectMemberModal from '../../projects/components/forms/create-project-member-modal';
import { getProjectMemberGridColumns } from '../../common/grid-columns';
import {
  EXISTING_PROJECT_STATUS_CHANGE_APPROVALS,
  GET_PROJECT,
  GET_PROJECT_ROLES,
  UPDATE_PROJECT_STATUS
} from '../gql';
import ApprovalText from '../../common/widgets/ApprovalTextComponent';
import { INextStatus, IProjectMembersDataTest, IProjectRole } from '../type';
import { UserRoleAppFormat } from '../../common/utils/enum';
import { isOperationsLeadOrSuperAdminOrAdmin } from '../../common/utils/helperFunctions';
import { IPermissions } from '../../common/utils/interfaces';
import ProjectDataFilter from './project-data-filter';
import ProjectDataGrid from './project-data-grid';

function SingleProjects(): JSX.Element {
  const { id = '' } = useParams();
  const { profileData } = useAppSelector((state) => state.profileData);
  const [permissions, setPermissions] = useState<IPermissions>({
    project: { create: false, read: false, update: false, delete: false },
    project_member: { create: false, read: true, update: false, delete: false }
  });

  const [isAdmin, setIsAdmin] = useState(false);
  const [projectDetails, setProjectDetails] = useState<{
    id: string;
    projectName: string;
    projectStatus: string;
    tentativeStartDate: string;
    tentativeEndDate: string;
    projectType: string;
    hypercarePeriod: number;
    projectManagerId?: string;
    members: IProjectMembersDataTest[];
    nextStatus: INextStatus[];
    createdById?: string;
  }>({
    id,
    projectName: 'Loading',
    projectStatus: '',
    tentativeStartDate: '',
    tentativeEndDate: '',
    projectType: '',
    hypercarePeriod: 0,
    projectManagerId: '',
    members: [],
    nextStatus: [],
    createdById: ''
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [actionUserId, setActionUserId] = useState<string>('');
  const [filteredMembers, setFilteredMembers] = useState<
    IProjectMembersDataTest[]
  >(projectDetails.members);
  const [projectRoles, setProjectRoles] = useState<IProjectRole[]>([
    { id: '0', displayName: 'None', role: 'None' }
  ]);
  const [hasExistingStatusApprovals, setHasExistingStatusApprovals] =
    useState<boolean>(false);
  const [isStatusDisable, setIsStatusDisable] = useState<boolean>(false);

  const {
    loading,
    data: projectData,
    refetch
  } = useQuery(GET_PROJECT, {
    variables: { id }
  });

  const { data: projectRoleData } = useQuery(GET_PROJECT_ROLES);

  const { data: statusApprovalRequests } = useQuery(
    EXISTING_PROJECT_STATUS_CHANGE_APPROVALS,
    { variables: { projectId: projectDetails.id } }
  );

  const [setStatus] = useMutation(UPDATE_PROJECT_STATUS);

  useEffect(() => {
    if (projectData) {
      setProjectDetails(projectData.getProject);
      setFilteredMembers(projectData.getProject.members);
    }
  }, [projectData]);

  useEffect(() => {
    if (id && profileData) {
      if (
        profileData?.projectPermissions &&
        profileData.projectPermissions[id]
      ) {
        setPermissions(profileData.projectPermissions[id]);
      } else if (profileData.permissions) {
        const projectPermissions = profileData.permissions.project;
        if (
          projectPermissions.create &&
          projectPermissions.update &&
          projectPermissions.delete &&
          projectPermissions.read
        ) {
          setIsAdmin(true);
        }
      }
    }
  }, [profileData]);

  useEffect(() => {
    const getDeliveryResponsible = (): void => {
      projectDetails.members.forEach((user) => {
        user.memberRoles.map(
          (role) =>
            role.projectRole.role === 'DELIVERY_RESPONSIBLE' &&
            setProjectDetails((prevState) => ({
              ...prevState,
              projectManagerId: user.member?.id
            }))
        );
      });
    };
    getDeliveryResponsible();
  }, [projectDetails.members]);

  useEffect(() => {
    if (profileData) {
      if (profileData.memberId) {
        setActionUserId(profileData.memberId);
      }
    }
  }, [profileData]);

  const options = [
    { id: 0, label: 'Initiated', color: '#FD0000', value: 'INITIATED' },
    { id: 1, label: 'Executing', color: '#00BF7A', value: 'ONGOING' },
    {
      id: 2,
      label: 'Hypercare Support',
      color: '#FD30B8',
      value: 'HYPERCARE_SUPPORT'
    },
    { id: 3, label: 'Completed', color: '#FBCD27CF', value: 'COMPLETED' },
    { id: 4, label: 'Discontinued', color: '#FC8800CF', value: 'DISCONTINUED' }
  ];

  const getStatusLabel = (status: string): string => {
    const statusOption = options.find((option) => option.value === status);
    return statusOption ? statusOption.label : '';
  };

  const getStatusColor = (status: string): string => {
    const statusOption = options.find((option) => option.value === status);
    return statusOption ? statusOption.color : '';
  };

  const updateProjectMemberCallback = async (): Promise<void> => {
    await refetch();
  };

  useEffect(() => {
    if (projectRoleData) {
      const allRoles = [...projectRoles, ...projectRoleData.getProjectRoles];
      setProjectRoles(allRoles);
    }
  }, [projectRoleData]);

  const handleStatusChange = async (status: string): Promise<void> => {
    setIsStatusDisable(true);
    if (
      permissions.project.update ||
      isOperationsLeadOrSuperAdminOrAdmin(profileData?.roles)
    ) {
      setStatus({
        variables: {
          actionUserId,
          id,
          projectName: projectDetails.projectName,
          projectStatus: status,
          tentativeStartDate: projectDetails.tentativeStartDate,
          tentativeEndDate: projectDetails.tentativeEndDate,
          projectType: projectDetails.projectType,
          hypercarePeriod: projectDetails.hypercarePeriod,
          projectManagerId: projectDetails.projectManagerId
        }
      })
        .then(async () => {
          if (
            (status === 'HYPERCARE_SUPPORT' || status === 'DISCONTINUED') &&
            !profileData?.roles.includes(UserRoleAppFormat.OperationsLead)
          ) {
            successToast('Request sent for Approvals');
            setHasExistingStatusApprovals(true);
          } else {
            successToast('Project Status Updated Successfully');
          }
          setProjectDetails((prevState) => ({
            ...prevState,
            projectStatus: status
          }));
          void updateProjectMemberCallback();
          setIsStatusDisable(false);
        })
        .catch(() => {
          errorToast(errorMessages.ErrormessageEditingProject);
        });
    }
  };

  const handleFilterChange = (filters: {
    searchText: string;
    selectedRole: string;
  }): void => {
    const { searchText, selectedRole } = filters;
    let newFilteredMembers = [...projectDetails.members];

    if (selectedRole && selectedRole !== 'None') {
      newFilteredMembers = newFilteredMembers.filter((member) =>
        member.memberRoles.some(
          (role) => role.projectRole.role === selectedRole
        )
      );
    }

    if (searchText) {
      const normalizedSearchText = searchText.toLowerCase();
      newFilteredMembers = newFilteredMembers.filter((member) =>
        member.member.displayName?.toLowerCase().includes(normalizedSearchText)
      );
    }

    setFilteredMembers(newFilteredMembers);
  };

  useEffect(() => {
    if (statusApprovalRequests) {
      const approvalRequests = statusApprovalRequests.checkPendingApprovals;
      if (approvalRequests.length !== 0) {
        const lastApprovalRequest =
          approvalRequests[approvalRequests.length - 1];
        const metadata = JSON.parse(lastApprovalRequest.metadata);

        setProjectDetails((prevState) => ({
          ...prevState,
          projectStatus: metadata.prjectStatus
        }));
        setHasExistingStatusApprovals(true);
      }
    }
  }, [projectDetails.projectStatus, statusApprovalRequests]);

  const isStatusAllowed = (status: string): boolean => {
    return projectDetails.nextStatus.some((ns) => ns.nextState === status);
  };

  return (
    <Box
      data-testid={'project-container'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        allignItems: 'flex-start',
        padding: '21px 20px',
        height: 'calc(100vh - 75px)',
        margin: '0px 22px 20px 22px',
        borderRadius: '12px',
        gap: '10px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '11px',
          color: '#46464F',
          alignItems: 'flex-start'
        }}
      >
        <Typography sx={{ fontSize: '28px', fontWeight: 700 }}>
          {projectDetails.projectName}
        </Typography>
        <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
          {'User Management > Projects > ' + projectDetails.projectName}
        </Typography>
      </Box>
      <Box
        sx={{
          color: '#FFFFFF',
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        {(permissions.project_member.create ||
          projectDetails.createdById === profileData?.id ||
          profileData?.permissions?.project_member?.create) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              sx={{
                background: '#295DAB',
                borderRadius: '100px',
                textTransform: 'none'
              }}
              data-testid="add-member-button"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '14px',
                  fontWeight: '500',
                  lineHeight: '20px',
                  letterSpacing: '0.10000000149011612px',
                  textAlign: 'center'
                }}
              >
                + Add Member
              </Typography>
            </Button>
            <CreateProjectMemberModal
              isVisible={isCreateModalOpen}
              closeRequestForm={() => setIsCreateModalOpen(false)}
              openRequestForm={() => setIsCreateModalOpen(true)}
              projectId={id}
              updateProjectMemberCallback={updateProjectMemberCallback}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <ProjectDataFilter
          projectRoles={projectRoles}
          onFilterChange={handleFilterChange}
        />
        <Box>
          {hasExistingStatusApprovals ||
          !(
            permissions.project_member.update ||
            projectDetails.createdById === profileData?.id ||
            profileData?.permissions?.project_status?.update
          ) ? (
            <>
              <Box
                sx={{
                  width: '238px',
                  height: '48px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #BDBDBD',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(
                        projectDetails.projectStatus
                      ),
                      marginRight: '10px',
                      marginLeft: '0px'
                    }}
                    data-testid="project-status-dropdown"
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '14px',
                      fontWeight: '500',
                      lineHeight: '20px',
                      letterSpacing: '0.10000000149011612px',
                      textAlign: 'center'
                    }}
                  >
                    {getStatusLabel(projectDetails.projectStatus)}
                  </Typography>
                </Box>
              </Box>
              {hasExistingStatusApprovals && (
                <Box sx={{ marginTop: '5px' }}>
                  <ApprovalText
                    label={'Waiting for Approval'}
                    color={'#D1DCFB'}
                  />
                </Box>
              )}
            </>
          ) : (
            <Select
              value={projectDetails.projectStatus}
              disabled={isStatusDisable}
              onChange={(e) => {
                void handleStatusChange(e.target.value);
              }}
              sx={{
                width: '238px',
                height: '48px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px'
              }}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.value}
                  disabled={!isStatusAllowed(option.value)}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: option.color,
                        marginRight: '10px',
                        marginLeft: '0px'
                      }}
                      data-testid="project-status-dropdown"
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Be Vietnam Pro',
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '20px',
                        letterSpacing: '0.10000000149011612px',
                        textAlign: 'center'
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>
      {permissions?.project_member.read && (
        <ProjectDataGrid
          columns={getProjectMemberGridColumns({
            canEdit:
              permissions?.project_member.update ||
              projectDetails.createdById === profileData?.id ||
              profileData?.permissions?.project_member?.update,
            canDelete:
              permissions?.project_member.delete ||
              projectDetails.createdById === profileData?.id ||
              profileData?.permissions?.project_member?.delete,
            isAdmin
          })}
          projectId={id ?? ''}
          isLoading={loading}
          data={filteredMembers}
          updateProjectMemberCallback={updateProjectMemberCallback}
          canEdit={
            permissions?.project_member.update ||
            projectDetails.createdById === profileData?.id ||
            profileData?.permissions?.project_member?.update
          }
          canDelete={
            permissions?.project_member.delete ||
            projectDetails.createdById === profileData?.id ||
            profileData?.permissions?.project_member?.delete
          }
          isAdmin={isAdmin}
        />
      )}
    </Box>
  );
}
export default SingleProjects;
